import { Component, forwardRef, Injector, Input, Output, ViewChild } from '@angular/core';
import { NG_VALIDATORS, NG_VALUE_ACCESSOR } from '@angular/forms';

import { Observable, Subject, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

import { MatAutocompleteTrigger } from '@angular/material/autocomplete';

import { AutocompleteComponent } from '@components/autocomplete/autocomplete.component';
import { UserApi } from '@api/user.api';
import { EMPTY_AUTOCOMPLETE_VALUE } from '@configs/autocomplete';
import { AutocompleteValue, EmptyAutocompleteValue } from '@models/autocomplete';
import { Researcher } from '@models/user';

@Component({
  selector: 'app-assignee-autocomplete',
  templateUrl: './assignee-autocomplete.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef((): any => AssigneeAutocompleteComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: AssigneeAutocompleteComponent,
      multi: true
    }
  ]
})
export class AssigneeAutocompleteComponent extends AutocompleteComponent {
  @ViewChild(MatAutocompleteTrigger) autocompleteTriggerComp!: MatAutocompleteTrigger;

  @Input() public override label: string = 'Researcher';
  @Input() public override placeholder: string = 'Search for a researcher...';

  @Output() openedChanged: Subject<boolean> = new Subject<boolean>();

  public readOnly: boolean = false;

  /**
   * Override base behavior: assignee suggestions are server-side and must cancel
   * stale requests while user keeps typing.
   */
  protected override readonly switchMap: boolean = true;

  constructor(injector: Injector, private api: UserApi) {
    super(injector);
  }

  public displayFn = (researcher: Researcher): string => {
    return researcher ? researcher.displayName : '';
  };

  public onOpenedChanged(isOpened: boolean): void {
    this.openedChanged.next(isOpened);
  }

  public openPanel(): void {
    this.autocompleteTriggerComp.openPanel();
  }

  /**
   * Override base input handling for object values.
   * Base component treats object input as "filter current allItems", but for assignees
   * we need to query API even for initial object value so panel has real options.
   */
  protected override onInputValueChange(query: string | AutocompleteValue): Observable<Array<AutocompleteValue>> {
    if (query && !this.utilitiesService.isString(query) && (query as Researcher).displayName) {
      return this.searchByQuery((query as Researcher).displayName);
    }

    return super.onInputValueChange(query);
  }

  /**
   * Override base no-op search with assignee-specific API lookup.
   */
  protected override searchByQuery(query: string): Observable<Array<AutocompleteValue>> {
    return this.api.searchForUsersByRole('researcher', { keyword: query }).pipe(
      tap((researchers: Array<Researcher>): Array<Researcher> => this.allItems = researchers),
      map((): Array<AutocompleteValue> => this.composeAutocompleteValues()),
      catchError((): Observable<Array<EmptyAutocompleteValue>> => of(EMPTY_AUTOCOMPLETE_VALUE))
    );
  }
}
